/**
 * c-cart-rewards.js — keeps the cart in line with the rewards ladder.
 *
 * Two things Shopify will not do on its own:
 * - Apply the ladder's discount code (ACTIVATE). The lander hands it to Shopify
 *   in its checkout URL; here it is applied to the cart itself, so the drawer
 *   shows the real discounted total instead of an estimate.
 * - Add the gift. A "Buy X Get Y" automatic discount only zeroes a gift that is
 *   already in the cart; it never adds it.
 *
 * The element re-runs on every cart render: `assets/cart.js` replaces the inner
 * HTML of `#MiniCart-<section>`, which destroys and recreates this element, so
 * `connectedCallback` is the reconcile hook. A pass that changes the cart asks
 * for a re-render, and the following pass finds the cart already correct and
 * stops — that is what terminates the cycle.
 */
(() => {
  const ROOT = (window.theme && window.theme.routes) || {};
  const CART_URL = ROOT.cart_url || '/cart';
  const ADD_URL = ROOT.cart_add_url || '/cart/add.js';
  const CHANGE_URL = ROOT.cart_change_url || '/cart/change.js';
  const UPDATE_URL = ROOT.cart_update_url || '/cart/update.js';

  /**
   * Variants that could not be added (most often sold out — the Canadian gift
   * runs on a small, capped stock). Retrying those on every render would spin
   * the cart, so a failure is remembered for the life of the page.
   */
  const unavailable = new Set();

  let inFlight = false;

  /**
   * The ladder code is applied at most once per page view. If Shopify drops it
   * (an excluded product, a cart that does not qualify), retrying on every
   * render would spin the cart.
   */
  let codeAttempted = false;

  /** The next render came from an add-to-cart: flash the checkout button. */
  let flashPending = false;

  /** Marks this module's own cart writes so the fetch hook below ignores them. */
  const INTERNAL_HEADER = 'X-C-Cart-Rewards';

  /* ------------------------------------------------------------------------
   * Refresh plumbing.
   *
   * `assets/cart.js` only ever listens for `cart:refresh`, but the theme's
   * add-to-cart call sites grew apart and now dispatch four different things —
   * `cart:refresh`, `cart:updated`, `cart:item-added`, `cart:open-drawer` — and
   * three of them dispatch nothing at all. That never showed because the Corner
   * Cart app watched the cart itself instead of trusting these events.
   *
   * Rather than edit ten shared files, the aliases are normalised here and any
   * direct call to a cart endpoint is picked up from `fetch`, which is what the
   * app was effectively doing.
   * --------------------------------------------------------------------- */
  const REFRESH_ALIASES = ['cart:updated', 'cart:item-added', 'cart:open-drawer'];
  const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  let refreshTimer = null;
  let pendingOpen = false;

  /** Collapses the several signals one click can produce into a single render. */
  const requestRefresh = (open) => {
    pendingOpen = pendingOpen || open === true;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      const shouldOpen = pendingOpen;
      pendingOpen = false;
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: shouldOpen } }));
    }, 150);
  };

  // Any refresh that opens the drawer is an add.
  document.addEventListener('cart:refresh', (event) => {
    if (event.detail && event.detail.open) flashPending = true;
  });

  for (const name of REFRESH_ALIASES) {
    document.addEventListener(name, (event) => {
      // `cart.js` emits `cart:updated` with the cart attached *after* it has
      // already swapped the panel; re-rendering again would be pure waste.
      if (event.detail && event.detail.cart) return;
      requestRefresh(true);
    });
  }

  /* ------------------------------------------------------------------------
   * Instant feedback.
   *
   * Shopify takes 0.7–1.7 s to answer /cart/add.js on this store, and the
   * drawer then needs its own render, so a click used to look dead for about
   * a second and a half. The drawer now opens the moment an add starts, in a
   * loading state, and fills in when the render lands.
   * --------------------------------------------------------------------- */
  let loadingTimer = null;

  const cartDrawer = () => document.querySelector('cart-drawer#CartDrawer');

  const stopLoading = () => {
    clearTimeout(loadingTimer);
    const drawer = cartDrawer();
    if (drawer) drawer.classList.remove('apc-loading');
  };

  const startLoading = () => {
    const drawer = cartDrawer();
    if (!drawer) return;
    drawer.classList.add('apc-loading');
    if (!drawer.open) drawer.show();
    clearTimeout(loadingTimer);
    // Never leave the drawer greyed out if no render ever comes back.
    loadingTimer = setTimeout(stopLoading, 8000);
  };

  /**
   * The header icon's `cart-count` only listens to the theme's pubsub, which
   * the `cart:refresh` path never publishes, so it stayed at 0 after an add.
   */
  const syncThemeCount = (count) => {
    document.querySelectorAll('cart-count').forEach((element) => {
      element.innerText = count;
      element.hidden = count === 0;
    });
  };

  if (!window.__cCartRewardsFetchHooked) {
    window.__cCartRewardsFetchHooked = true;
    const nativeFetch = window.fetch;

    window.fetch = function (input, init) {
      const result = nativeFetch.apply(this, arguments);

      try {
        const url = String(typeof input === 'string' ? input : (input && input.url) || '');
        const headers = (init && init.headers) || {};
        const isOurs = Boolean(headers[INTERNAL_HEADER]);

        if (!isOurs && CART_WRITE.test(url)) {
          // Only an add should pop the drawer open; a quantity change or a
          // clear just repaints whatever is on screen.
          const isAdd = /\/cart\/add/.test(url);
          if (isAdd) startLoading();

          result
            .then((response) => {
              if (response && response.ok) requestRefresh(isAdd);
              else stopLoading();
            })
            .catch(stopLoading);
        }
      }
      catch (error) {
        /* Never let the hook break a cart request. */
      }

      return result;
    };
  }

  /** Every code on the cart, whatever shape this cart payload carries them in. */
  const appliedCodes = (cart) => {
    if (Array.isArray(cart.discount_codes)) return cart.discount_codes.map((code) => code.code);
    return (cart.cart_level_discount_applications || [])
      .filter((application) => application.type === 'discount_code')
      .map((application) => application.title);
  };

  /**
   * A line an automatic discount made free is a gift, not a purchase. The store
   * runs other gift-with-purchase promos that add lines on their own, and those
   * must not move the ladder. Mirrors the rule in c-cart-drawer.liquid.
   */
  const isFreeGift = (item) => item.final_line_price === 0 && item.original_line_price > 0;

  /* ------------------------------------------------------------------------
   * Grouped rows.
   *
   * The drawer folds lines Shopify split for a discount back into one row
   * (see c-cart-drawer.liquid). `cart.js` changes one line at a time, so a
   * change on such a row is rewritten into a single `/cart/update.js` call:
   * the whole quantity goes on the row's anchor line and the other lines are
   * zeroed. Shopify re-splits on its side if a promo still needs it. Ungrouped
   * rows go through `cart.js` untouched.
   * --------------------------------------------------------------------- */
  /* ------------------------------------------------------------------------
   * "Switch to one-time" on subscription rows.
   *
   * Drops the selling plan from every line of the row. Shopify then merges it
   * with a one-time line of the same product, if there is one, and the
   * product starts counting toward the ladder.
   * --------------------------------------------------------------------- */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest && event.target.closest('#CartDrawer .apc-switch');
    if (!button) return;
    event.preventDefault();

    const keys = (button.dataset.lineKeys || '').split(',').filter(Boolean);
    if (keys.length === 0) return;

    button.disabled = true;
    const drawer = cartDrawer();
    if (drawer) drawer.classList.add('apc-loading');

    try {
      const cart = await (await fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } })).json();
      for (const key of keys) {
        const line = cart.items.find((item) => item.key === key);
        if (!line) continue;
        await postJSON(CHANGE_URL, { id: key, quantity: line.quantity, selling_plan: null });
      }
    }
    catch (error) {
      console.error('[c-cart-rewards] could not switch the line to one-time', error);
    }
    finally {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
    }
  });

  customElements.whenDefined('cart-items').then(() => {
    const CartItems = customElements.get('cart-items');
    if (CartItems.prototype.__cGroupedLines) return;
    CartItems.prototype.__cGroupedLines = true;

    const updateLine = CartItems.prototype.updateQuantity;

    CartItems.prototype.updateQuantity = function (line, quantity, name, target) {
      const row = Array.from(this.querySelectorAll('[data-anchor-key]'))
        .find((element) => element.dataset.anchorKey === line);
      const keys = row ? row.dataset.lineKeys.split(',').filter(Boolean) : [];
      if (keys.length < 2) return updateLine.call(this, line, quantity, name, target);

      this.enableLoading(line);

      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));

      const updates = {};
      for (const key of keys) updates[key] = key === line ? Number(quantity) : 0;

      const config = theme.utils.fetchConfig();
      config.headers[INTERNAL_HEADER] = '1';

      fetch(theme.routes.cart_update_url || '/cart/update.js', { ...config, body: JSON.stringify({ updates, sections }) })
        .then(async (response) => {
          const state = await response.json();
          if (!response.ok) state.errors = state.description || state.message || 'Error';
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart: state, target, line, name });
        })
        .catch((error) => console.error('[c-cart-rewards] could not update the grouped line', error));
    };
  });

  const toIds = (value) =>
    (value || '')
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter((id) => !Number.isNaN(id));

  const postJSON = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        // The gift is reconciled by this module and already asks for its own
        // render; the fetch hook must not queue a second one.
        [INTERNAL_HEADER]: '1'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const error = new Error(`${url} responded ${response.status}`);
      error.status = response.status;
      throw error;
    }

    return response.json();
  };

  class CartRewards extends HTMLElement {
    get threshold() {
      return parseInt(this.dataset.giftThreshold, 10) || 0;
    }

    get giftVariantId() {
      return parseInt(this.dataset.giftVariantId, 10) || 0;
    }

    get giftVariantIds() {
      return toIds(this.dataset.giftVariantIds);
    }

    get giftAvailable() {
      return this.dataset.giftAvailable === 'true';
    }

    get discountCode() {
      return (this.dataset.discountCode || '').trim();
    }

    connectedCallback() {
      stopLoading();
      this.flashCheckout();
      this.reconcile();
    }

    /**
     * Add-to-cart opens the drawer and the checkout button flashes gold three
     * times (the lander's behaviour). This element is recreated on every
     * render, so it is the place to pick up a pending flash.
     */
    flashCheckout() {
      if (!flashPending) return;
      const drawer = this.closest('cart-drawer');
      const button = drawer && drawer.querySelector('.apc-checkout');
      if (!button || button.closest('[hidden]')) return;

      flashPending = false;
      button.classList.remove('apc-checkout--flash');
      // Restart the animation even if a previous flash is still running.
      void button.offsetWidth;
      button.classList.add('apc-checkout--flash');
      setTimeout(() => button.classList.remove('apc-checkout--flash'), 2400);
    }

    async reconcile() {
      if (inFlight) return;

      const giftIds = this.giftVariantIds;
      if (giftIds.length === 0 && !this.discountCode) return;

      inFlight = true;

      try {
        const cart = await (await fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } })).json();
        const changed = await this.applyTo(cart, giftIds);

        if (changed) {
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
        }
      }
      catch (error) {
        console.error('[c-cart-rewards] could not reconcile the gift line', error);
      }
      finally {
        inFlight = false;
      }
    }

    /**
     * Decides and performs the single change the cart needs, if any.
     * Returns true when the cart was modified.
     */
    async applyTo(cart, giftIds) {
      const activeGiftId = this.giftVariantId;

      // Variants the drawer found qualifying when it rendered (Liquid knows
      // product tags and types, /cart.js does not). Absent = every product.
      const qualifyingIds = this.dataset.qualifyingVariantIds
        ? toIds(this.dataset.qualifyingVariantIds)
        : null;

      const qualifying = cart.items
        .filter((item) => !giftIds.includes(item.variant_id) && !isFreeGift(item))
        // Subscriptions don't count (the same variant can also sit on a
        // one-time line, so the variant list alone cannot tell them apart).
        .filter((item) => !item.selling_plan_allocation)
        .filter((item) => !qualifyingIds || qualifyingIds.includes(item.variant_id))
        .reduce((total, item) => total + item.quantity, 0);

      const giftLines = cart.items.filter((item) => giftIds.includes(item.variant_id));
      const earned = this.threshold > 0 && activeGiftId > 0 && qualifying >= this.threshold;

      // A gift from another market, or a second copy, must go before anything else.
      const stale = giftLines.find((item) => item.variant_id !== activeGiftId);
      if (stale) {
        await postJSON(CHANGE_URL, { id: stale.key, quantity: 0 });
        return true;
      }

      const current = giftLines.find((item) => item.variant_id === activeGiftId);

      if (!earned) {
        if (current) {
          await postJSON(CHANGE_URL, { id: current.key, quantity: 0 });
          return true;
        }
        return this.applyCode(cart, qualifying);
      }

      if (current) {
        if (current.quantity !== 1) {
          await postJSON(CHANGE_URL, { id: current.key, quantity: 1 });
          return true;
        }
        return this.applyCode(cart, qualifying);
      }

      if (this.giftAvailable && !unavailable.has(activeGiftId)) {
        try {
          await postJSON(ADD_URL, { items: [{ id: activeGiftId, quantity: 1 }] });
          return true;
        }
        catch (error) {
          // Out of stock or otherwise rejected: stop offering it for this page
          // view rather than retrying on every single cart render.
          unavailable.add(activeGiftId);
          console.warn('[c-cart-rewards] the gift could not be added', error);
        }
      }

      return this.applyCode(cart, qualifying);
    }

    /**
     * Puts the ladder code on the cart once it has something in it. A code
     * that is already there — the ladder's or one the customer brought from an
     * affiliate or promo link — is always left alone.
     */
    async applyCode(cart, qualifying) {
      const code = this.discountCode;
      if (!code || codeAttempted || qualifying === 0) return false;
      if (appliedCodes(cart).length > 0) return false;

      codeAttempted = true;
      try {
        await postJSON(UPDATE_URL, { discount: code });
        return true;
      }
      catch (error) {
        console.warn('[c-cart-rewards] the discount code could not be applied', error);
        return false;
      }
    }
  }

  /**
   * The header count, e.g. "· 3 items", or the bare number on the floating
   * button (`data-format="number"`).
   *
   * It sits in the drawer header, outside `#MiniCart-<section>`, so it survives
   * every panel re-render and has to keep itself up to date. The theme's own
   * `cart-count` is not reused here because it overwrites its text with the bare
   * number, which would drop the noun.
   */
  class CartCountLabel extends HTMLElement {
    connectedCallback() {
      this.onRefresh = () => this.pull();
      document.addEventListener('cart:refresh', this.onRefresh);

      const pubsub = window.theme && window.theme.pubsub;
      if (pubsub) {
        this.unsubscribe = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
          const cart = event.cart;
          if (!cart || cart.errors) return;
          // An add returns the added line, not the cart; only a full cart can
          // have its gift subtracted.
          if (Array.isArray(cart.items) && typeof cart.item_count === 'number') this.render(this.countOf(cart));
          else this.pull();
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('cart:refresh', this.onRefresh);
      if (this.unsubscribe) this.unsubscribe();
    }

    async pull() {
      try {
        const cart = await (await fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } })).json();
        this.render(this.countOf(cart));
        syncThemeCount(cart.item_count);
      }
      catch (error) {
        console.error('[c-cart-rewards] could not refresh the cart count', error);
      }
    }

    /** Units the customer chose — the gift line never counts. */
    countOf(cart) {
      const giftIds = toIds(this.dataset.giftVariantIds);
      const giftUnits = cart.items
        .filter((item) => giftIds.includes(item.variant_id) || isFreeGift(item))
        .reduce((total, item) => total + item.quantity, 0);
      return cart.item_count - giftUnits;
    }

    render(count) {
      const previous = parseInt(this.textContent, 10) || 0;

      if (this.dataset.format === 'number') {
        this.textContent = String(count);
      }
      else {
        const noun = count === 1 ? this.dataset.singular : this.dataset.plural;
        this.textContent = `${count} ${noun}`;
      }
      this.hidden = count === 0;

      if (count > previous) {
        this.classList.remove('bump');
        void this.offsetWidth;
        this.classList.add('bump');
      }
    }
  }

  if (!customElements.get('c-cart-rewards')) {
    customElements.define('c-cart-rewards', CartRewards);
  }

  if (!customElements.get('c-cart-count')) {
    customElements.define('c-cart-count', CartCountLabel);
  }
})();
